import styled from "@emotion/styled";
import { toast } from "react-toastify";
import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { getFieldAreaHectares, useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import { useConvert, useLang, useWeight, useYield, useFormatArea } from "@src/hooks/useLang";
import { useNavigate } from "react-router-dom";
import { Column, Row } from "@src/components/Flex";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import Button from "@components/Button";
import { ReactComponent as MinimalizeIcon } from "@assets/icons/minimalize.svg";
import { ReactComponent as ExpandIcon } from "@assets/icons/expand.svg";
import { ReactComponent as DownloadIcon } from "@assets/icons/download.svg";
import { ReactComponent as FieldIcon } from "@assets/icons/map.svg";
import { ReactComponent as EvenOutIcon } from "@assets/icons/evenOut.svg";
import useOnClickOutside from "@src/hooks/useOnClickOutside";
import useIsMobile, { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import LeftBarSettingsMenuItem from "@components/Layout/LeftBar/LeftBarSettingsMenuItem";
// @ts-ignore
import * as FileSaver from "file-saver";
import BN from "@src/utils/BN";
import { useStores } from "@stores";
import { WeightUnit } from "@services/demosService";
import SyncYieldToDeereDialog from "@screens/FieldsScreen/dialogs/SyncYieldToDeereDialog";
import { eveningStrengthLabel } from "@screens/FieldsScreen/YieldEvenPanel";
import { getYieldDeereSyncStatus, YieldSyncStatus } from "@services/fieldsService";

interface IProps { }

const Root = styled.div<{ width?: number; compact?: boolean }>`
  position: absolute;
  left: 24px;
  right: 16px;
  bottom: 16px;
  background: #ffffff;
  border-radius: 6px;
  padding: 16px;
  box-sizing: border-box;
  z-index: 11;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    left: 12px;
    right: 12px;
    /* Sit above the floating "Fields" pill (40px tall, 16px off the tab bar)
       instead of underneath it. */
    bottom: 68px;
    /* Collapsed on a phone the panel is just a title bar — tighten it so it
       doesn't eat the map. */
    padding: ${({ compact }) => (compact ? "10px 12px" : "16px")};
  }
`;

const Title = styled(Text)`
  font-family: "Nunito Sans";
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 26px;
  color: #494c5b;
  white-space: nowrap;

  /* Mobile only: collapsed, the name shares one line with the control buttons,
     so clip it instead of wrapping the panel into a tall block. On desktop the
     parts must keep their natural width — min-width: 0 there lets them shrink
     and truncates the name/area for no reason. */
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ControlsWrapper = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-direction: row;
  max-width: fit-content;
  position: absolute;
  top: 16px;
  right: 16px;
  & > :first-of-type {
    margin-right: 8px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    top: ${({ compact }) => (compact ? "10px" : "16px")};
    right: ${({ compact }) => (compact ? "12px" : "16px")};
  }
`;

const ControlButton = styled(Button)`
  height: 24px;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

/* "Even out yield": labelled on desktop, icon-only on a phone where the row
   shares the line with the field name. */
const EvenButton = styled(ControlButton)`
  width: auto;
  gap: 4px;
  margin-left: 8px;
  padding: 0 10px 0 6px;

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 36px;
    padding: 0;
  }
`;

const EvenLabel = styled.span`
  font-size: 12px;
  white-space: nowrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

/* Width of the even button on a phone (36px + its 8px margin), reserved in the
   title row so the pinned controls never sit on the field name. */
const EVEN_BUTTON_MOBILE_RESERVE = 44;

const Body = styled(Column)`
  width: 100%;

  /* The 4-column table can't fit a phone width — scroll it inside the panel. */
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Table = styled.table`
  width: 100%;
  &,
  th,
  td {
    padding: 7px 12px;
    border: 1px solid #ebeeef;
    border-collapse: collapse;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: 560px;
  }
`;

const TitleRow = styled(Row)<{ compact?: boolean; extraReserve?: number }>`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    /* Collapsed: a single line with just the field name, so it never wraps into
       the 5-line block the area/crop parts used to produce. */
    flex-wrap: ${({ compact }) => (compact ? "nowrap" : "wrap")};
    width: ${({ compact }) => (compact ? "100%" : "fit-content")};
    min-width: 0;
    overflow: hidden;
    /* Keep the field name clear of the absolutely-pinned control buttons. */
    padding-right: ${({ extraReserve }) => 112 + (extraReserve ?? 0)}px;
  }
`;

const Tooltip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: absolute;
  transition: 0.4s;
  overflow: hidden;
  //height: 92px;
  top: 0;
  background: #ffffff;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.2);
  border-radius: 6px 6px 6px 0;
`;

/* Always one line. The name/area/crop parts beside it are Text items, which are
   width: 100%, so in the title row a shrinkable chip got squeezed to its
   longest word ("Evened / · / Strong"). Where the row wraps (expanded on a
   phone) the chip moves down whole instead. */
const SyncChip = styled.span<{ tone: "queued" | "ok" | "fail" }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  white-space: nowrap;
  gap: 4px;
  margin-left: 8px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  border-radius: 999px;
  cursor: pointer;
  background: ${(p) =>
    p.tone === "ok" ? "#E6F4EA" : p.tone === "fail" ? "#FCE8E6" : "#FEF7E0"};
  color: ${(p) =>
    p.tone === "ok" ? "#137333" : p.tone === "fail" ? "#A50E0E" : "#7C5E00"};
`;


const ReductionInput = styled.input`
  width: 96px;
  height: 32px;
  box-sizing: border-box;
  border: 1px solid #ebeeef;
  border-radius: 4px;
  padding: 0 12px;
  /* Match the table cell text: Nunito Sans, bold, 14px */
  font-family: "Nunito Sans", sans-serif;
  font-weight: bold;
  font-size: 14px;
  line-height: 22px;
  color: #494c5b;
  outline: none;
  &:focus {
    border-color: #68ad18;
  }
`;

const formatExportURL = (
  field_id: string,
  field_name: string | null,
  format: string,
  seasonId: string | undefined,
  units: { weight: string; area: string; poundsPerBushel: number; poundsPerBag: number },
): string => {
  const BASE_URL = process.env.REACT_APP_API_URL ?? window.location.origin;
  const q = new URLSearchParams();

  q.append("format", format);
  if (field_name) {
    q.append("field_name", field_name.replaceAll(" ", "_"));
  }
  if (seasonId) {
    q.append("seasonId", seasonId);
  }
  // Pass unit preferences so the backend can convert yields
  q.append("weight", units.weight);
  q.append("area", units.area);
  q.append("poundsPerBushel", units.poundsPerBushel.toString());
  q.append("poundsPerBag", units.poundsPerBag.toString());

  return `${BASE_URL}/api/v1/fields/${field_id}/yield/export?${q.toString()}`;
};

const MapYieldStatistics: React.FC<IProps> = () => {
  const vm = useFieldsScreenVM();
  const lang = useLang();
  const locWeight = useWeight();
  const formatArea = useFormatArea();
  const locYield = useYield();
  const { convertYield, convertWeight } = useConvert();
  const { settingsStore, seasonStore, accountStore } = useStores();
  const navigate = useNavigate();

  const stats =
    vm.selectedFieldId != null
      ? vm.heatmapStatistics[String(vm.selectedFieldId)]
      : undefined;

  const ref = React.useRef(null);
  const isMobile = useIsMobile();
  // On a phone the stats table covers most of the map, so the panel starts
  // collapsed there and opens on demand.
  const [expanded, setExpanded] = useState(!isMobile);
  const [tooltipOpened, setTooltipOpened] = useState(false);
  // Collapsed on mobile the panel shows the field name and the control buttons
  // and nothing else — area/crop/sync chips are what made it wrap into a tall
  // block that ate the map.
  const compact = isMobile && !expanded;

  useOnClickOutside(ref, () => setTooltipOpened(false));

  const id = vm.selectedField?.get("id");
  const name = vm.selectedField?.get("name");
  const externalId = vm.selectedField?.get("externalId") as string | undefined;
  const isJohnDeereLinked =
    typeof externalId === "string" && externalId.startsWith("johndeere:");

  const area = formatArea(getFieldAreaHectares(vm.selectedField)) || null;

  const cropId = vm.selectedField?.get("culture_id");
  const crop = vm.crops.find(({ id }) => id === cropId);

  const seasonId = seasonStore.currentSeasonId;

  // ── Dirt/moisture % (clean-weight reduction) ──
  const fieldReduction =
    (vm.selectedField?.get("reduction_percent") as number | undefined) ?? 0;
  const [dirtInput, setDirtInput] = useState<string>(String(fieldReduction));
  const [savingReduction, setSavingReduction] = useState(false);
  // Re-sync the input whenever the field (or its saved value) changes.
  useEffect(() => {
    setDirtInput(String(fieldReduction));
  }, [id, fieldReduction]);

  const parsedDirt = parseFloat(dirtInput);
  // Treat an empty/invalid input as 0 so reverting to 0 still counts as a change.
  // Negative values are allowed: they add weight back instead of deducting it,
  // which is how a scale that under-reports gets corrected upward.
  const clampedDirt = isNaN(parsedDirt) ? 0 : Math.min(100, Math.max(-100, parsedDirt));
  const dirtChanged = clampedDirt !== fieldReduction;

  const handleSaveReduction = async () => {
    if (!id || !dirtChanged || savingReduction) return;
    setSavingReduction(true);
    try {
      await vm.updateFieldReduction(id, clampedDirt);
    } finally {
      setSavingReduction(false);
    }
  };

  // ── John Deere sync state ──
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncStatus, setSyncStatus] = useState<YieldSyncStatus | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshSyncStatus = React.useCallback(() => {
    if (!isJohnDeereLinked || !id || !seasonId) {
      setSyncStatus(null);
      return;
    }
    getYieldDeereSyncStatus(id, seasonId)
      .then(setSyncStatus)
      .catch(() => setSyncStatus(null));
  }, [isJohnDeereLinked, id, seasonId]);

  useEffect(() => {
    refreshSyncStatus();
  }, [refreshSyncStatus]);

  // Light polling: only when status is QUEUED. Every 6s for up to 3 min.
  useEffect(() => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    if (syncStatus?.status !== "QUEUED") return;
    let elapsed = 0;
    const tick = () => {
      elapsed += 6000;
      if (elapsed > 180_000) return;
      getYieldDeereSyncStatus(id!, seasonId!)
        .then((s) => {
          setSyncStatus(s);
          if (s.status === "QUEUED") {
            pollTimerRef.current = setTimeout(tick, 6000);
          }
        })
        .catch(() => {
          pollTimerRef.current = setTimeout(tick, 6000);
        });
    };
    pollTimerRef.current = setTimeout(tick, 6000);
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [syncStatus?.status, id, seasonId]);

  // No room at half width; the evening panel carries its own numbers.
  if (vm.isSplitView) return null;

  // ── Yield evening ──
  // Same permission as the dirt/moisture Save and the toolbar's zone tool.
  const evened = vm.selectedFieldEvened;
  const showEvenButton = accountStore.canEdit && !evened;
  const evening = vm.selectedFieldEvening;
  const evenedChipLabel = evening
    ? `${lang.fields.yieldEvenChip} · ${eveningStrengthLabel(lang.fields, evening.strength)}`
    : lang.fields.yieldEvenChip;

  return vm.selectedFieldView === "yield" && stats ? (
    <Root compact={compact}>
      <ControlsWrapper ref={ref} compact={compact}>
        <Tooltip
          style={{
            right: "0.75em",
            top: tooltipOpened ? "-9em" : 0,
            width: tooltipOpened ? 208 : 0,
            height: tooltipOpened ? "9em" : 0
          }}
        >
          {[
            { name: "Shapefile", format: "shp", ext: "shp.zip" },
            { name: "John Deere", format: "deere", ext: "deere.zip" },
            { name: "CSV", format: "csv", ext: "csv" },
          ].map((item) => {
            return (
              <LeftBarSettingsMenuItem
                key={item.format}
                title={item.name}
                onClick={() => {
                  setTooltipOpened(false);
                  const url = formatExportURL(id, name, item.format, seasonId, settingsStore.units);
                  fetch(url)
                    .then((response) => {
                      if (!response.ok) {
                        throw new Error(`Unexpected response status: ${response.status}`);
                      }
                      return response.blob();
                    })
                    .then((blob) => {
                      const safeName = (name ?? "field").replaceAll(" ", "_");
                      FileSaver.saveAs(blob, `${safeName}.${item.ext}`);
                    })
                    .catch((e) => toast.error(e?.message ?? "Export failed"));
                }}
              />
            );
          })}
          {isJohnDeereLinked && (
            <LeftBarSettingsMenuItem
              key="sync-deere"
              title="Sync to Operations Center"
              onClick={() => {
                setTooltipOpened(false);
                setSyncDialogOpen(true);
              }}
            />
          )}
        </Tooltip>
        {settingsStore.features.prescription_maps && (
          <ControlButton kind="secondary" onClick={() => {
            navigate("/prescription/" + id, { state: { field_name: name } });
          }}>
            <FieldIcon />
          </ControlButton>
        )}
        {expanded ? (
          <ControlButton kind="secondary" onClick={() => setExpanded(false)}>
            <MinimalizeIcon />
          </ControlButton>
        ) : (
          <ControlButton kind="secondary" onClick={() => setExpanded(true)}>
            <ExpandIcon />
          </ControlButton>
        )}

        {accountStore.canExport && (
          <ControlButton kind="secondary" onClick={() => {
            setTooltipOpened(!tooltipOpened);
          }}>
            <DownloadIcon />
          </ControlButton>
        )}

        {showEvenButton && (
          <EvenButton
            kind="secondary"
            onClick={vm.toggleYieldEven}
            title={lang.fields.yieldEvenTitle}
            aria-label={lang.fields.yieldEvenTitle}
            aria-pressed={vm.isYieldEvenOpen}
          >
            <EvenOutIcon />
            <EvenLabel>{lang.fields.yieldEvenTitle}</EvenLabel>
          </EvenButton>
        )}
      </ControlsWrapper>
      <TitleRow
        mainAxisSize="fit-content"
        alignItems="center"
        compact={compact}
        extraReserve={showEvenButton ? EVEN_BUTTON_MOBILE_RESERVE : 0}
      >
        {(compact
          ? [name]
          : [name, area, crop?.name])
          .filter(Boolean)
          .map((part, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <Title style={{ color: "rgb(204, 213, 216)" }}>&nbsp;•&nbsp;</Title>
              )}
              <Title>{part}</Title>
            </React.Fragment>
          ))}
        {!compact && isJohnDeereLinked && syncStatus?.status === "QUEUED" && (
          <SyncChip
            tone="queued"
            title="Operations Center is processing this layer."
            onClick={() => setSyncDialogOpen(true)}
          >
            Syncing…
          </SyncChip>
        )}
        {!compact && isJohnDeereLinked && syncStatus?.status === "VALID" && (
          <SyncChip
            tone="ok"
            title={syncStatus.syncedAt ? `Synced ${new Date(syncStatus.syncedAt).toLocaleString()}` : "Synced to Operations Center"}
            onClick={() => setSyncDialogOpen(true)}
          >
            <span>Synced</span>
            <span style={{ display: "inline-flex", lineHeight: 1 }}>✓</span>
          </SyncChip>
        )}
        {!compact && isJohnDeereLinked && (syncStatus?.status === "INVALID" || syncStatus?.status === "NO_FILE_RESOURCE") && (
          <SyncChip
            tone="fail"
            title={syncStatus.error ?? (syncStatus.status === "NO_FILE_RESOURCE" ? "File resource missing in John Deere" : "John Deere couldn't process this layer")}
            onClick={() => setSyncDialogOpen(true)}
          >
            Sync failed
          </SyncChip>
        )}
        {!compact && evened && (
          <SyncChip
            tone="ok"
            title={lang.fields.yieldEvenTitle}
            onClick={accountStore.canEdit ? vm.openYieldEven : undefined}
            style={accountStore.canEdit ? undefined : { cursor: "default" }}
          >
            {evenedChipLabel}
          </SyncChip>
        )}
      </TitleRow>
      {isJohnDeereLinked && id && (
        <SyncYieldToDeereDialog
          fieldId={id}
          fieldName={name}
          seasonId={seasonId}
          externalId={externalId}
          avgYieldDisplay={
            stats
              ? `${new BN(convertYield(stats.avg_value || 0)).toFormat(3)} ${locYield()}`
              : undefined
          }
          visible={syncDialogOpen}
          onClose={() => setSyncDialogOpen(false)}
          onSynced={refreshSyncStatus}
        />
      )}
      {expanded && (
        <Body>
          <SizedBox height={24} />
          <Table>
            <thead>
              <tr>
                <th>
                  <Text color="#494C5B" type="heading" size={14}>
                    {lang.fields.statsCropTitle}
                  </Text>
                </th>
                <th>
                  <Text color="#494C5B" type="heading" size={14}>
                    {`${lang.fields.statsAvg}, ${locYield()}`}
                  </Text>
                </th>
                <th>
                  <Text color="#494C5B" type="heading" size={14}>
                    {`${lang.fields.statsTotal}, ${locWeight()}`}
                  </Text>
                </th>
                <th>
                  <Text color="#494C5B" type="heading" size={14}>
                    {lang.fields?.dirtMoisturePercent ?? "Dirt / moisture, %"}
                  </Text>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>
                  <Text color="#494C5B" type="heading" size={14}>
                    {crop?.name}
                  </Text>
                </th>
                <th>
                  <Text color="#494C5B" type="heading" size={14}>
                    {new BN(convertYield(stats?.avg_value || 0)).toFormat(3)}
                  </Text>
                </th>
                <th>
                  <Text color="#494C5B" type="heading" size={14}>
                    {new BN(convertWeight(stats?.total_value || 0, WeightUnit.Kilogram)).toFormat(3)}
                  </Text>
                </th>
                <th>
                  <Row alignItems="center" mainAxisSize="fit-content">
                    <ReductionInput
                      type="number"
                      min={-100}
                      max={100}
                      value={dirtInput}
                      disabled={!accountStore.canEdit}
                      onChange={(e) => setDirtInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveReduction();
                      }}
                    />
                    <SizedBox width={8} />
                    {/* Always reserve the button's space so showing/hiding it
                        never shifts the input — toggle visibility instead. */}
                    <Button
                      kind="primary"
                      style={{
                        width: "auto",
                        height: 32,
                        padding: "0 16px",
                        visibility: dirtChanged && accountStore.canEdit ? "visible" : "hidden",
                      }}
                      disabled={!dirtChanged || savingReduction || !accountStore.canEdit}
                      onClick={handleSaveReduction}
                    >
                      {savingReduction ? "…" : (lang.fields?.drawFieldsOkButton ?? "Save")}
                    </Button>
                  </Row>
                </th>
              </tr>
            </tbody>
          </Table>
        </Body>
      )}
    </Root>
  ) : null;
};
export default observer(MapYieldStatistics);
